<template>
  <div
    :class="[
      {
        'tw-w-full': fullWidth,
        'tw-relative': !noRelative,
      },
      panelMargin,
    ]"
  >
    <!-- top error messages -->
    <div v-if="errorMessage && errorPos === 'top'" class="tw-flex tw-flex-col">
      <panel-error :message="errorMessage"></panel-error>
    </div>
    <div
      :id="anchorId"
      class="tw-absolute tw--top-20 tw-h-px tw-w-full"
      style="z-index: -1"
    />

    <!-- header title -->
    <header
      v-if="$slots.header && slots['default']"
      class="tw-relative"
      :class="{ 'tw-mb-4': panelOpen }"
    >
      <component
        :is="hasCollapse ? 'button' : 'div'"
        class="tw-w-full tw-outline-none focus:game-link-color-hover"
        :class="{
          'tw-flex tw-justify-between tw-items-center hover:game-link-color-hover mod-button-transition':
            hasCollapse,
        }"
        @click="togglePanel"
      >
        <div class="tw-w-full tw-flex tw-items-center tw-justify-between">
          <div class="tw-w-full tw-flex tw-items-center">
            <slot name="header" />

            <font-awesome-icon
              v-if="hasCollapse"
              :icon="panelOpen ? 'angle-up' : 'angle-down'"
              size="lg"
              class="tw-ml-2 sm:tw-ml-4"
            />
          </div>

          <!-- super admin / back to top pill -->
          <base-panel-pill v-if="pillText" :label="pillText" />
        </div>
      </component>

      <div
        :class="{
          'tw-absolute tw--bottom-2 xs:tw--bottom-3 sm:tw--bottom-5 tw-left-0 tw-h-px tw-w-full tw-bg-grey tw-opacity-40':
            !panelOpen,
        }"
      />
    </header>

    <div
      v-if="panelOpen"
      class="tw-w-full"
      :class="[
        {
          'tw-pt-4': pillText && noPadding,
          'tw-relative': !noRelative,
        },
        noPadding ? 'tw-rounded-tr-none' : 'mod-base-panel-px',
        panelBorderColor,
        panelBorderWidth,
        panelBgStyles,
      ]"
      v-bind="$attrs"
    >
      <div
        :class="{
          'tw-opacity-40 tw-border-transparent tw-pointer-events-none':
            disabled,
        }"
      >
        <base-panel-pill
          v-if="pillText && $slots.header && !slots['default']"
          :label="pillText"
          class="tw-absolute tw--top-1.5 sm:tw--top-5 tw--right-1.5 sm:tw-right-2"
          :class="[panelBorderColor, panelBorderWidth]"
        />
        <!-- inner header title -->
        <header
          v-if="$slots.header && !slots['default']"
          :class="[panelHeaderYPadding]"
        >
          <slot name="header" />
        </header>

        <div
          v-if="slots['default'] || $slots.noHtmlContent || $slots.content"
          class="tw-flex tw-flex-col"
          :class="{ 'tw-py-6': !noPadding }"
        >
          <!-- inner header content -->
          <div
            v-if="$slots.noHtmlContent || $slots.content"
            :class="{ 'tw-mb-8': contentMargin }"
            class="mod-content"
          >
            <p v-if="$slots.noHtmlContent">
              <slot name="noHtmlContent" />
            </p>
            <slot v-else name="content" />
          </div>

          <!-- panel content -->
          <slot :watch-slot="watchSlot" />

          <!-- error messages -->
          <div
            v-if="errorMessage && errorPos === 'bottom'"
            class="tw-flex tw-flex-col tw-pt-4"
          >
            <panel-error :message="errorMessage"></panel-error>
          </div>
        </div>
      </div>

      <!-- footer -->
      <footer
        v-if="$slots.footer"
        class="tw-border-theme-1 tw-py-6"
        :class="noPadding ? 'tw-pb-0' : 'tw-border-t'"
      >
        <div class="tw-w-full tw-flex tw-justify-between tw-items-center">
          <slot name="footer" />
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance, computed, toRefs, ref } from 'vue'
import BasePanelPill from '@components/Misc/BasePanelPill.vue'
import PanelError from '@components/Error/PanelError.vue'
import { apiErrorsToArray } from '@helpers/utils.js'
import { useSlots } from '@composables'

export default {
  components: {
    BasePanelPill,
    PanelError,
  },
  inheritAttrs: false,
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Object],
      default: null,
    },
    errorPos: {
      type: String,
      default: 'bottom',
      validator: (val) => ['top', 'bottom'].includes(val),
    },
    primary: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    pillText: {
      type: String,
      default: null,
    },
    contentMargin: {
      type: Boolean,
      default: true,
    },
    info: {
      type: Boolean,
      default: false,
    },
    anchorId: {
      type: String,
      default: '',
    },
    noHeaderPadding: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    hasCollapse: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: true,
    },
    extraSmallMargin: {
      type: Boolean,
      default: false,
    },
    smallMargin: {
      type: Boolean,
      default: false,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    noRelative: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
    lightBackground: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const {
      extraSmallMargin,
      noHeaderPadding,
      lightBackground,
      hasCollapse,
      smallMargin,
      noPadding,
      noMargin,
      pillText,
      primary,
      success,
      warning,
      loading,
      danger,
      error,
      info,
      open,
    } = toRefs(props)

    const { slots, watchSlot } = useSlots(getCurrentInstance().slots)

    const panelOpen = ref(open.value)

    const errorMessage = computed(() => {
      return apiErrorsToArray(error.value)
    })

    const panelBorderColor = computed(() => {
      if (primary.value) {
        return 'tw-border-primary'
      } else if (success.value) {
        return 'tw-border-success'
      } else if (danger.value) {
        return 'tw-border-danger'
      } else if (warning.value) {
        return 'tw-border-warning'
      } else if (info.value || pillText.value) {
        return 'tw-border-info'
      } else {
        return 'tw-border-transparent'
      }
    })

    const requireStyles = computed(
      () =>
        primary.value ||
        success.value ||
        danger.value ||
        warning.value ||
        info.value
    )

    const panelBorderWidth = computed(() => 'tw-border-2')

    const panelBgStyles = computed(() => {
      if (noPadding.value) {
        return ''
      } else if (loading.value) {
        return 'tw-bg-theme-3 tw-opacity-80'
      } else if (requireStyles.value) {
        return 'tw-bg-black-opacity'
      } else if (lightBackground.value) {
        return 'game-tile-background-color'
      }

      return 'tw-bg-theme-3'
    })

    const panelMargin = computed(() => {
      if (noMargin.value) {
        return ''
      }
      return smallMargin.value
        ? 'tw-mb-6 xs:tw-mb-8'
        : extraSmallMargin.value
          ? 'tw-mb-2 xs:tw-mb-4'
          : 'tw-mb-8'
    })

    const panelHeaderYPadding = computed(() => {
      if (noHeaderPadding.value) {
        return ''
      }
      return 'tw-py-6'
    })

    function togglePanel() {
      if (hasCollapse.value) {
        panelOpen.value = !panelOpen.value
      } else {
        panelOpen.value = true
      }
    }

    return {
      panelHeaderYPadding,
      panelBorderWidth,
      panelBorderColor,
      panelBgStyles,
      errorMessage,
      togglePanel,
      panelMargin,
      panelOpen,
      watchSlot,
      slots,
    }
  },
}
</script>
